import React, {useState, useEffect, useCallback} from 'react';
import './Requisites.scss';
import {useNavigate} from 'react-router-dom';
import {format} from 'date-fns';
import {useSelector} from 'react-redux';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import Toast from '../../components/Toast/Toast';
import RequisiteFormModal from '../../components/RequisiteFormModal/RequisiteFormModal';
import Pagination from '../../components/Layout/Pagination';
import PageToolbar from '../../components/Layout/PageToolbar';
import CopyButton from '../../components/CopyButton/CopyButton';
import { useTranslation } from 'react-i18next';

// Утилита: извлекает текст из HTML строки
const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};


// Маппинг статусов реквизитов
const STATUS_CONFIG = {
    '1':  { label: 'В работе',       color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   textColor: '#22c55e' },
    '2':  { label: 'Ожидает',        color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  textColor: '#f59e0b' },
    '7':  { label: 'Нет баланса',    color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)',  textColor: '#8b5cf6' },
    '34': { label: 'Врем. лимит',    color: '#ec4899', bg: 'rgba(236,72,153,0.12)',  textColor: '#ec4899' },
    '4':  { label: 'Лимит',          color: '#be185d', bg: 'rgba(190,24,93,0.12)',   textColor: '#be185d' },
    '5':  { label: 'Архив',          color: '#6b7280', bg: 'rgba(107,114,128,0.12)', textColor: '#6b7280' },
    '6':  { label: 'Блок',           color: '#ef4444', bg: 'rgba(239,68,68,0.15)',   textColor: '#ef4444' },
    '8':  { label: 'Макс. заявок',   color: '#ec4899', bg: 'rgba(236,72,153,0.12)',  textColor: '#ec4899' },
    '9':  { label: 'Лимит в сутки',  color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  textColor: '#3b82f6' },
};

// Маппинг цветов валют для тегов
const CURRENCY_COLORS = {
    'RUB': { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6' },
    'UAH': { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
    'KZT': { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e' },
};

// Парсинг total_amount HTML
const parseTotalAmount = (html) => {
    if (!html) return { count: '0', completed: '0', filled: '0', limit: '0', remaining: '0' };
    const text = stripHtml(html);

    const countMatch = text.match(/Кол-во выполненных:\s*(\d+)/);
    const completedMatch = text.match(/Выполнено:\s*([\d\s.,]+\s*\w*)/);
    const filledMatch = text.match(/Заполнено:\s*([\d\s.,]+\s*\w*)/);
    const limitMatch = text.match(/Лимит:\s*([\d\s.,]+\s*\w*)/);
    const remainingMatch = text.match(/Остаток:\s*([\d\s.,]+\s*\w*)/);

    return {
        count: countMatch ? countMatch[1].trim() : '0',
        completed: completedMatch ? completedMatch[1].trim() : '0',
        filled: filledMatch ? filledMatch[1].trim() : '0',
        limit: limitMatch ? limitMatch[1].trim() : '0',
        remaining: remainingMatch ? remainingMatch[1].trim() : '0',
    };
};

export default function Requisites() {

    const { t } = useTranslation('requisites');
    const theme = useSelector((state) => state.APP_THEME);
    const api_url = useSelector((state) => state.API_URL);
    const available_payment_settings = useSelector((state) => state.USER_PAYMENTS);
    const user_can_set_min_orders = useSelector((state) => state.USER_CAN_SET_MIN_ORDERS);
    const canDeleteOwnRequisites = useSelector((state) => state.USER_CAN_DELETE_OWN_REQUISITES);
    const novodroid_token = useSelector((state) => state.NOVODROID_TOKEN);
    const novodroid_url = useSelector((state) => state.NOVODROID_URL);
    const bank_currencies = useSelector((state) => state.BANK_CURRENCIES);
    const default_bank_currency = useSelector((state) => state.DEFAULT_BANK_CURRENCY);
    const banks_without_card = useSelector((state) => state.BANKS_WITHOUT_CARD);
    const banks_with_iban = useSelector((state) => state.BANKS_WITH_IBAN);
    const userRole = useSelector((state) => state.USER_ROLE);
    const isTeamlead = userRole === 'teamlead';

    const navigate = useNavigate();
    const jwtToken = localStorage.getItem('accessToken');

    useEffect(() => {
        if (!jwtToken) {
            navigate('/auth');
            localStorage.clear();
        }
    }, [jwtToken, navigate]);

    // start of states

    const [requisitesData, setRequisitesData] = useState([]);
    const [columns, setColumns] = useState([]);

    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [statusValues, setStatusValues] = useState([]);
    const [autoClose, setAutoClose] = useState('');
    const [searchNumber, setSearchNumber] = useState('');

    const [listOfKinds, setListOfKinds] = useState([]);

    const [errorFetchingRequisities, setErrorFetchingRequisities] = useState(false);
    const [errorSubmit, setErrorSubmit] = useState(false);
    const [errorSubmitText, setErrorSubmitText] = useState('');
    const [successSubmit, setSuccessSubmit] = useState(false);

    const [pageSize, setPageSize] = useState(6);
    const [pageIndex, setPageIndex] = useState(1);
    const [reqRowsCount, setReqRowsCount] = useState(0);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);

    // end of states

    const fieldNames = {
        credentials: t('requisites:fieldNames.credentials'),
        credentials_limit: t('requisites:fieldNames.credentials_limit'),
        min_rur: t('requisites:fieldNames.min_rur'),
        max_rur: t('requisites:fieldNames.max_rur'),
        max_success_requests_per_day: t('requisites:fieldNames.max_success_requests_per_day'),
        max_requests_in_range: t('requisites:fieldNames.max_requests_in_range'),
        __all__: t('requisites:fieldNames.__all__')
    };

    const handleStatusClick = (status, value) => {
        if (selectedStatuses.includes(status)) {
            setSelectedStatuses(selectedStatuses.filter(item => item !== status));
            setStatusValues(statusValues.filter(item => item !== value));
        } else {
            setSelectedStatuses([...selectedStatuses, status]);
            setStatusValues([...statusValues, value]);
        }
    };

    const generateColumns = useCallback((data) => {
        if (data.length > 0) {
            const firstItem = data[0];
            const generatedColumns = Object.keys(firstItem).slice(1).map(key => ({
                id: key,
                label: firstItem[key]?.label || key,
            }));
            setColumns(generatedColumns);
        }
    }, []);

    const getRequestsData = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + `exchange/managercredentials/info/?page=${pageIndex}&page_size=${pageSize}&status__in=${statusValues.join(',')}&search=${searchNumber}&can_autoclose=${autoClose}`, {
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/auth');
                }
            }
            const data = await response.json();
            setRequisitesData(data.results);
            generateColumns(data.results);
            setReqRowsCount(data.count);
        } catch (error) {
            setErrorFetchingRequisities(true);
        }
    }, [api_url, pageIndex, pageSize, statusValues, searchNumber, autoClose, navigate, generateColumns]);

    useProtectedInterval(getRequestsData, [successSubmit, pageIndex, pageSize, statusValues, searchNumber, autoClose]);

    const handleClearFilters = () => {
        setSelectedStatuses([]);
        setStatusValues([]);
        setAutoClose('');
        setSearchNumber('');
    }

    const handleChangeStatus = async (item, status) => {
        const data = {
            credentials: item.credentials.value,
            status: status,
        };

        try {
            const response = await fetch(api_url + `exchange/managercredentials/${item.pk}` + '/', {
                method: 'PUT',
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            if (response.ok) {
                setSuccessSubmit(true);
            } else {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/auth');
                } else if (response.status === 400) {
                    const errorData = await response.json();
                    const formattedErrors = Object.entries(errorData)
                        .map(([key, messages]) => {
                            const fieldName = fieldNames[key] || key;
                            return `${fieldName} - ${messages.join('\n')}`;
                        })
                        .join('\n');

                    setErrorSubmit(true);
                    setErrorSubmitText(formattedErrors);
                } else {
                    setErrorSubmit(true);
                    setErrorSubmitText(t('common:unknownError'));
                }
            }
        } catch (error) {
            setErrorSubmit(true);
        }
    };

    const handleSoftDelete = async (item) => {
        if (!window.confirm(t('requisites:confirmDelete'))) {
            return;
        }

        try {
            const response = await fetch(api_url + `exchange/managercredentials/${item.pk}/soft-delete/`, {
                method: 'POST',
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                setRequisitesData((items) => items.filter((current) => current.pk !== item.pk));
                setReqRowsCount((count) => Math.max(0, count - 1));
                setSuccessSubmit(true);
                return;
            }

            const errorData = await response.json().catch(() => ({}));
            setErrorSubmit(true);
            setErrorSubmitText(errorData.text || t('common:unknownError'));
        } catch (error) {
            setErrorSubmit(true);
            setErrorSubmitText(t('common:unknownError'));
        }
    };

    const getKindsData = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + `exchange/managercredentials/?fields=kind`, {
                method: 'OPTIONS',
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/auth');
                }
            }
            const data = await response.json();
            const banks = data.actions.POST.kind.choices;
            const formattedBanks = banks.map(bank => [bank.value, bank.display_name]);
            setListOfKinds(formattedBanks);
        } catch (error) {
        }
    }, [api_url, navigate]);

    useEffect(() => {
        getKindsData();
    }, [getKindsData]);

    // Modal handlers
    const openCreateModal = () => {
        if (!available_payment_settings || available_payment_settings.length === 0) {
            setErrorSubmit(true);
            setErrorSubmitText(t('forms:requisiteForm.noPaymentMethodsAssigned'));
            return;
        }
        setEditData(null);
        setModalOpen(true);
    };

    const openEditModal = (item) => {
        setEditData(item);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setEditData(null);
    };

    const handleModalSuccess = () => {
        setModalOpen(false);
        setEditData(null);
        setSuccessSubmit(true);
    };

    return (
        <div className={theme === 'light' ? "Requisites" : "Requisites dark"}>
            <Toast show={errorFetchingRequisities} type="error" message={t('requisites:errors.fetchError')} onClose={() => setErrorFetchingRequisities(false)} />
            <Toast show={errorSubmit} type="error" message={errorSubmitText} onClose={() => setErrorSubmit(false)} />
            <Toast show={successSubmit} type="success" message={t('common:dataSuccessfullySent')} onClose={() => setSuccessSubmit(false)} />
            <div className="content">
                <PageToolbar
                    onAdd={isTeamlead ? undefined : openCreateModal}
                    addLabel={t('requisites:addLabel')}
                    filters={[
                        {label: t('requisites:filters.working'), value: 'В работе'},
                        {label: t('requisites:filters.waiting'), value: 'Ожидает'},
                        {label: t('requisites:filters.noBalance'), value: 'Недостаточно баланса'},
                        {label: t('requisites:filters.tempLimit'), value: 'Временный лимит'},
                        {label: t('requisites:filters.cardLimit'), value: 'Лимит по карте'},
                        {label: t('requisites:filters.archive'), value: 'Архив'},
                        {label: t('requisites:filters.blocked'), value: 'Блок'},
                        {label: t('requisites:filters.maxRequests'), value: 'Максимум заявок за диапазон'},
                        {label: t('requisites:filters.dayLimit'), value: 'Лимит в сутки'}
                    ]}
                    onFilterChange={(filter) => {
                        const statusMap = {
                            'В работе': '1',
                            'Ожидает': '2',
                            'Недостаточно баланса': '3',
                            'Временный лимит': '34',
                            'Лимит по карте': '4',
                            'Архив': '5',
                            'Блок': '6',
                            'Максимум заявок за диапазон': '8',
                            'Лимит в сутки': '9'
                        };
                        handleStatusClick(filter, statusMap[filter]);
                    }}
                    activeFilters={selectedStatuses}
                    searchValue={searchNumber}
                    onSearchChange={setSearchNumber}
                    searchPlaceholder={t('requisites:searchPlaceholder')}
                    onClearAll={handleClearFilters}
                    extraButtons={
                        <div className="auto">
                            <span>{t('requisites:autocloseColon')}</span>
                            <div className="buttons">
                                <button
                                    className={(autoClose !== '' && autoClose && theme === 'light') ? 'selected' : (autoClose !== '' && autoClose && theme === 'dark') ? 'darkselected' : ''}
                                    onClick={() => setAutoClose(autoClose === true ? '' : true)}
                                >
                                    {t('common:yes')}
                                </button>
                                <button
                                    className={(autoClose !== '' && !autoClose && theme === 'light') ? 'selected' : (autoClose !== '' && !autoClose && theme === 'dark') ? 'darkselected' : ''}
                                    onClick={() => setAutoClose(autoClose === false ? '' : false)}
                                >
                                    {t('common:no')}
                                </button>
                            </div>
                        </div>
                    }
                />
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={reqRowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[6, 9, 12]}
                />
                <div className="req_cards_wrapper">
                    <div className="req_cards">
                        {requisitesData.map((item) => {
                            const statusVal = item?.status?.value;
                            const statusConf = STATUS_CONFIG[statusVal] || { label: statusVal, color: '#6b7280', bg: 'rgba(107,114,128,0.12)', textColor: '#6b7280' };
                            const currency = item?.currency_code?.value || '';
                            const currencyColor = CURRENCY_COLORS[currency] || { bg: 'rgba(107,114,128,0.1)', color: '#6b7280' };
                            const paymentType = item?.payment_type?.value || '';
                            const totalAmount = parseTotalAmount(item?.total_amount?.value);

                            return (
                                <div className="card_req" key={item.pk}>
                                    {/* 1. Шапка: статус + теги */}
                                    <div className="card-header">
                                        <span
                                            className="card-status-badge"
                                            style={{ background: statusConf.bg, color: statusConf.textColor }}
                                        >
                                            {t('requisites:statuses.' + statusVal) || statusVal}
                                        </span>
                                        <div className="card-tags">
                                            {currency && (
                                                <span
                                                    className="card-tag"
                                                    style={{ background: currencyColor.bg, color: currencyColor.color }}
                                                >
                                                    {currency}
                                                </span>
                                            )}
                                            {paymentType && (
                                                <span className="card-tag card-tag--method">
                                                    {paymentType}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* 2. Реквизиты: номер карты + копирование + подсказка */}
                                    <div className="card-credentials">
                                        <div className="card-credentials-row">
                                            <span className="card-credentials-number">{item?.credentials?.value}</span>
                                            <CopyButton text={item?.credentials?.value || ''} size="small" />
                                        </div>
                                        {item?.comment?.value && (
                                            <span className="card-credentials-hint">{item?.comment?.value}</span>
                                        )}
                                    </div>

                                    {/* 3. Лимиты: компактная сетка */}
                                    <div className="card-limits">
                                        <div className="card-limits-item">
                                            <span className="card-limits-label">{t('requisites:card.minMax')}</span>
                                            <span className="card-limits-value">
                                                {Number(item?.min_rur?.value).toLocaleString()} / {Number(item?.max_rur?.value).toLocaleString()} {currency}
                                            </span>
                                        </div>
                                        <div className="card-limits-item">
                                            <span className="card-limits-label">{t('requisites:card.limit')}</span>
                                            <span className="card-limits-value">{totalAmount.limit}</span>
                                        </div>
                                        <div className="card-limits-item">
                                            <span className="card-limits-label">{t('requisites:card.remaining')}</span>
                                            <span className="card-limits-value">{totalAmount.remaining}</span>
                                        </div>
                                    </div>

                                    {/* 4. Настройки: маленькие индикаторы */}
                                    <div className="card-settings">
                                        <span className={`card-setting-item ${item?.can_autoclose?.value ? 'enabled' : 'disabled'}`}>
                                            <span className="card-setting-dot" />
                                            {t('requisites:autoclose')}
                                        </span>
                                        {item?.can_autoclose?.value && (
                                            <span className={`card-setting-item ${item?.last4digits?.value ? 'enabled' : 'disabled'}`}>
                                                <span className="card-setting-dot" />
                                                {item?.last4digits?.value ? `*${item.last4digits.value}` : t('requisites:card.noCard')}
                                            </span>
                                        )}
                                        <span className="card-setting-item neutral">
                                            {item?.max_requests_in_range?.value === 0
                                                ? t('requisites:card.noLimitMin')
                                                : `${item?.max_requests_in_range?.value} / ${item?.range_in_minutes?.value} ${t('requisites:card.min')}`}
                                        </span>
                                        <span className="card-setting-item neutral">
                                            {item?.max_success_requests_per_day?.value === 0
                                                ? t('requisites:card.noLimitDay')
                                                : `${item?.max_success_requests_per_day?.value} / ${t('requisites:card.day')}`}
                                        </span>
                                        {item?.reservation_range_percent?.value > 0 && (
                                            <span className="card-setting-item neutral">
                                                {t('requisites:card.reserve')} {item?.reservation_range_percent?.value}%
                                            </span>
                                        )}
                                    </div>

                                    {/* 5. Оборот: компактная статистика */}
                                    <div className="card-turnover">
                                        <div className="card-turnover-item">
                                            <span className="card-turnover-label">{t('requisites:card.completed')}</span>
                                            <span className="card-turnover-value">{totalAmount.completed}</span>
                                        </div>
                                        <div className="card-turnover-item">
                                            <span className="card-turnover-label">{t('requisites:card.filled')}</span>
                                            <span className="card-turnover-value">{totalAmount.filled}</span>
                                        </div>
                                        <div className="card-turnover-item">
                                            <span className="card-turnover-label">{t('requisites:card.count')}</span>
                                            <span className="card-turnover-value">{totalAmount.count}</span>
                                        </div>
                                    </div>

                                    {/* 6. Даты */}
                                    <div className="card-dates">
                                        <span className="card-date">
                                            <span className="card-date-label">{t('common:created')}</span>
                                            {format(new Date(item?.created?.value), 'dd.MM.yyyy HH:mm')}
                                        </span>
                                        <span className="card-date">
                                            <span className="card-date-label">{t('common:modified')}</span>
                                            {format(new Date(item?.modify?.value), 'dd.MM.yyyy HH:mm')}
                                        </span>
                                    </div>

                                    {/* 7. Действия */}
                                    <div className="card-actions">
                                        {statusVal === '2' && !isTeamlead && (
                                            <button
                                                className="req-action-btn action-activate"
                                                onClick={() => handleChangeStatus(item, '1')}
                                            >
                                                {t('requisites:actions.activate')}
                                            </button>
                                        )}
                                        {statusVal === '1' && (
                                            <button
                                                className="req-action-btn action-deactivate"
                                                onClick={() => handleChangeStatus(item, '2')}
                                            >
                                                {t('requisites:actions.deactivate')}
                                            </button>
                                        )}
                                        {!isTeamlead && (
                                            <button
                                                className="req-action-btn action-edit"
                                                onClick={() => openEditModal(item)}
                                            >
                                                {t('requisites:actions.edit')}
                                            </button>
                                        )}
                                        {canDeleteOwnRequisites && !isTeamlead && (
                                            <button
                                                className="req-action-btn action-delete"
                                                onClick={() => handleSoftDelete(item)}
                                            >
                                                {t('requisites:actions.delete')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={reqRowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[6, 9, 12]}
                />

                <RequisiteFormModal
                    isOpen={modalOpen}
                    onClose={handleModalClose}
                    editData={editData}
                    onSuccess={handleModalSuccess}
                    apiUrl={api_url}
                    availablePayments={available_payment_settings}
                    userCanSetMinOrders={user_can_set_min_orders}
                    theme={theme}
                    novodroidToken={novodroid_token}
                    novodroidUrl={novodroid_url}
                    bankKinds={listOfKinds}
                    bankCurrencies={bank_currencies}
                    defaultBankCurrency={default_bank_currency}
                    banksWithoutCard={banks_without_card}
                    banksWithIban={banks_with_iban}
                />
            </div>
        </div>
    )
}
